import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Smartphone, 
  WifiOff, 
  Bell, 
  Zap,
  Share,
  PlusSquare,
  Download,
  Check,
  ArrowDown
} from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { PostInstallHelperCard } from "@/components/PostInstallHelperCard";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "lupuslogix_pwa_prompt_dismissed";
const JUST_SIGNED_UP_KEY = "lupuslogix_just_signed_up";
const PENDING_PROMPT_KEY = "lupuslogix_pending_pwa_prompt";
const PENDING_PLAN_KEY = "lupuslogix_pending_plan";
const POST_INSTALL_SHOWN_KEY = "lupuslogix_post_install_shown";
const INSTALLED_FLAG_KEY = "lupuslogix_pwa_installed";
const DISMISSAL_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface PWAOnboardingPromptProps {
  /** Force show regardless of dismissal state (for testing) */
  forceShow?: boolean;
}

export function PWAOnboardingPrompt({ forceShow = false }: PWAOnboardingPromptProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showPostInstallHelp, setShowPostInstallHelp] = useState(false);
  const { isInstalled, isIOS, deferredPrompt, promptInstall } = usePWAInstall();
  const { user } = useAuth();
  const isAndroid = !isIOS && /android/i.test(navigator.userAgent);

  useEffect(() => {
    // Don't show if already installed
    if (isInstalled) return;

    // Don't show if the PWA was ever installed on this device — the
    // one-time PostInstallConfirmationDialog owns that surface. Prevents
    // a stray install prompt from re-appearing after the user installs.
    if (!forceShow) {
      try {
        if (
          localStorage.getItem(POST_INSTALL_SHOWN_KEY) === "true" ||
          localStorage.getItem(INSTALLED_FLAG_KEY) === "true"
        ) {
          return;
        }
      } catch { /* ignore */ }
    }

    // Don't show on legal/info pages — users often open these in a new tab
    // mid-checkout (Stripe's Terms link) and the install popup blocks the
    // page they came to read.
    if (!forceShow) {
      const p = window.location.pathname;
      if (
        p.startsWith("/terms") ||
        p.startsWith("/privacy") ||
        p.startsWith("/medical-disclaimer") ||
        p.startsWith("/data-breach-policy") ||
        p.startsWith("/ai-wellness-disclaimer") ||
        p.startsWith("/faq") ||
        p.startsWith("/install")
      ) {
        return;
      }
    }

    // Don't show on the dashboard for authenticated users — the Getting
    // Started Checklist already has its own "Install the app" row, so the
    // dialog is redundant and creates a "Got it → unchecked" perception bug.
    if (!forceShow && user && window.location.pathname.startsWith("/dashboard")) return;

    // Don't show while a Stripe checkout is pending / in-flight — the
    // Marketing page is about to redirect to Stripe and this dialog
    // would flash on top of the redirect.
    if (localStorage.getItem(PENDING_PLAN_KEY) && !forceShow) return;

    // Show for brand-new signups. Either flag qualifies:
    // - JUST_SIGNED_UP_KEY: set at signup, may be cleared by Index handoff
    // - PENDING_PROMPT_KEY: longer-lived marker set during handoff so the
    //   prompt still fires when the user lands on /dashboard
    const justSignedUp =
      localStorage.getItem(JUST_SIGNED_UP_KEY) ||
      localStorage.getItem(PENDING_PROMPT_KEY);
    if (!justSignedUp && !forceShow) return;

    // Check if dismissed recently
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt && !forceShow) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < DISMISSAL_DURATION) {
        return;
      }
    }

    // Show prompt after a brief delay to let the page load
    const timer = setTimeout(() => {
      setIsOpen(true);
      // One-shot: clear the signup markers as soon as we open, so if the user
      // navigates away without explicitly dismissing (route change, tab close)
      // the prompt does NOT re-appear on the next mount.
      localStorage.removeItem(JUST_SIGNED_UP_KEY);
      localStorage.removeItem(PENDING_PROMPT_KEY);
    }, 500);

    return () => clearTimeout(timer);
    // Re-check when the auth user changes (e.g. brand-new signup) so the
    // prompt fires on the same session where signup flags were just set.
  }, [isInstalled, forceShow, user?.id]);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    localStorage.removeItem(JUST_SIGNED_UP_KEY);
    localStorage.removeItem(PENDING_PROMPT_KEY);
    setIsOpen(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      const installed = await promptInstall();
      if (installed) {
        localStorage.removeItem(JUST_SIGNED_UP_KEY);
        localStorage.removeItem(PENDING_PROMPT_KEY);
        if (isAndroid) {
          setShowPostInstallHelp(true);
        } else {
          setIsOpen(false);
        }
      }
    }
  };

  // Don't render if already installed
  if (isInstalled && !forceShow) return null;

  const benefits = [
    { icon: Zap, label: "Faster loading" },
    { icon: Bell, label: "Reminders (online)" },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDismiss()}>
      <DialogContent className="sm:max-w-md overflow-hidden">
        <DialogHeader className="text-center sm:text-center">
          <motion.div 
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <Smartphone className="h-8 w-8 text-primary" />
          </motion.div>
          <DialogTitle className="text-xl">Install LupusLogix</DialogTitle>
          <DialogDescription className="text-base">
            Get the best experience with our app installed on your device
          </DialogDescription>
        </DialogHeader>

        {/* Animated Benefits */}
        <div className="flex justify-center gap-4 py-4">
          {benefits.map(({ icon: Icon, label }, index) => (
            <motion.div
              key={label}
              className="flex flex-col items-center gap-1.5 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
            >
              <motion.div 
                className="flex h-10 w-10 items-center justify-center rounded-full bg-muted"
                whileHover={{ scale: 1.1 }}
              >
                <Icon className="h-5 w-5 text-muted-foreground" />
              </motion.div>
              <span className="text-xs text-muted-foreground">{label}</span>
            </motion.div>
          ))}
        </div>

        {/* Platform-specific instructions with animations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {showPostInstallHelp ? (
            <PostInstallHelperCard onDismiss={() => setIsOpen(false)} />
          ) : isIOS ? (
            <IOSInstructions />
          ) : deferredPrompt ? (
            <AndroidDesktopInstructions onInstall={handleInstall} />
          ) : (
            <ManualInstructions />
          )}
        </motion.div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full"
          >
            Maybe Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IOSInstructions() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    { icon: Share, label: "Share", description: "Tap Share button" },
    { icon: PlusSquare, label: "Add", description: "Add to Home Screen" },
  ];

  return (
    <div className="space-y-4">
      {/* Animated Phone Mockup */}
      <div className="relative mx-auto h-40 w-24">
        {/* Phone frame */}
        <div className="absolute inset-0 rounded-2xl border-4 border-muted-foreground/30 bg-gradient-to-b from-muted to-muted/50">
          {/* Screen content */}
          <div className="absolute inset-2 rounded-lg bg-background overflow-hidden">
            {/* Safari-like bar at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-muted/80 flex items-center justify-around px-2">
              <AnimatePresence mode="wait">
                {activeStep === 0 && (
                  <motion.div
                    key="share"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="relative"
                  >
                    <motion.div
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                      className="p-1 rounded bg-primary/20"
                    >
                      <Share className="h-4 w-4 text-primary" />
                    </motion.div>
                    {/* Tap indicator */}
                    <motion.div
                      className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-primary"
                      animate={{ scale: [0, 1, 0] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Share sheet animation */}
            <AnimatePresence>
              {activeStep === 1 && (
                <motion.div
                  initial={{ y: 100 }}
                  animate={{ y: 0 }}
                  exit={{ y: 100 }}
                  className="absolute bottom-8 left-0 right-0 h-24 bg-muted rounded-t-lg p-2"
                >
                  <div className="flex flex-col gap-1">
                    <div className="h-2 w-12 bg-muted-foreground/20 rounded" />
                    <motion.div 
                      className="flex items-center gap-1 p-1 rounded bg-primary/20"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                    >
                      <PlusSquare className="h-3 w-3 text-primary" />
                      <span className="text-[6px] text-primary font-medium">Add to Home</span>
                    </motion.div>
                    <div className="h-2 w-14 bg-muted-foreground/20 rounded" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Success animation */}
            <AnimatePresence>
              {activeStep === 2 && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="absolute inset-0 flex items-center justify-center bg-primary/10"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.2, 1] }}
                    transition={{ duration: 0.4 }}
                    className="p-3 rounded-full bg-green-500/20"
                  >
                    <Check className="h-6 w-6 text-green-500" />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex justify-center gap-2">
        {steps.map((step, index) => (
          <motion.div
            key={step.label}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-colors",
              activeStep === index 
                ? "bg-primary text-primary-foreground" 
                : "bg-muted text-muted-foreground"
            )}
            animate={activeStep === index ? { scale: [1, 1.05, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            <step.icon className="h-3 w-3" />
            <span>{step.description}</span>
          </motion.div>
        ))}
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Note: iOS 16.4+ required for push notifications
      </p>
    </div>
  );
}

interface AndroidDesktopInstructionsProps {
  onInstall: () => void;
}

function AndroidDesktopInstructions({ onInstall }: AndroidDesktopInstructionsProps) {
  return (
    <div className="space-y-4">
      {/* Animated Install Illustration */}
      <div className="relative mx-auto h-32 w-32">
        {/* App icon */}
        <motion.div
          className="absolute top-0 left-1/2 -translate-x-1/2 h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg"
          animate={{ y: [0, 50, 50] }}
          transition={{ 
            duration: 2, 
            repeat: Infinity, 
            times: [0, 0.4, 1],
            ease: "easeInOut"
          }}
        >
          <span className="text-lg font-bold text-primary-foreground">L</span>
        </motion.div>

        {/* Arrow */}
        <motion.div
          className="absolute top-14 left-1/2 -translate-x-1/2"
          animate={{ opacity: [1, 1, 0], y: [0, 10, 10] }}
          transition={{ 
            duration: 2, 
            repeat: Infinity, 
            times: [0, 0.3, 0.4],
            ease: "easeInOut"
          }}
        >
          <ArrowDown className="h-6 w-6 text-primary" />
        </motion.div>

        {/* Home screen representation */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-16 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
          <motion.div
            className="grid grid-cols-3 gap-1"
            initial={{ opacity: 0.3 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.3 }}
          >
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className={cn(
                  "h-3 w-3 rounded",
                  i === 4 ? "bg-primary" : "bg-muted-foreground/20"
                )}
                initial={i === 4 ? { scale: 0 } : {}}
                animate={i === 4 ? { scale: [0, 1.2, 1] } : {}}
                transition={{ delay: 0.8, duration: 0.3 }}
              />
            ))}
          </motion.div>
        </div>
      </div>

      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          onClick={onInstall}
          className="w-full gap-2"
          size="lg"
        >
          <Download className="h-5 w-5" />
          Install Now
        </Button>
      </motion.div>
      <p className="text-center text-xs text-muted-foreground">
        One tap to add LupusLogix to your home screen
      </p>
    </div>
  );
}

function ManualInstructions() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % 2);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-4">
      {/* Animated browser mockup */}
      <div className="relative mx-auto h-28 w-40">
        {/* Browser frame */}
        <div className="absolute inset-0 rounded-lg border-2 border-muted-foreground/30 bg-muted">
          {/* Browser toolbar */}
          <div className="flex items-center justify-between h-6 px-2 border-b border-muted-foreground/20">
            <div className="flex gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-red-400" />
              <div className="h-1.5 w-1.5 rounded-full bg-yellow-400" />
              <div className="h-1.5 w-1.5 rounded-full bg-green-400" />
            </div>
            
            {/* Menu button */}
            <motion.div
              className={cn(
                "flex flex-col gap-0.5 p-1 rounded",
                step === 0 && "bg-primary/20"
              )}
              animate={step === 0 ? { scale: [1, 1.2, 1] } : {}}
              transition={{ repeat: Infinity, duration: 1 }}
            >
              <div className="h-0.5 w-2 bg-muted-foreground" />
              <div className="h-0.5 w-2 bg-muted-foreground" />
              <div className="h-0.5 w-2 bg-muted-foreground" />
            </motion.div>
          </div>

          {/* Browser content */}
          <div className="p-2 h-[calc(100%-1.5rem)] relative">
            <div className="space-y-1">
              <div className="h-2 w-3/4 bg-muted-foreground/10 rounded" />
              <div className="h-2 w-1/2 bg-muted-foreground/10 rounded" />
            </div>

            {/* Dropdown menu */}
            <AnimatePresence>
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -5 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -5 }}
                  className="absolute top-0 right-0 w-20 bg-background rounded border shadow-lg p-1"
                >
                  <div className="h-2 w-12 bg-muted-foreground/20 rounded mb-1" />
                  <motion.div
                    className="flex items-center gap-1 p-0.5 rounded bg-primary/20"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                  >
                    <Download className="h-2 w-2 text-primary" />
                    <span className="text-[5px] text-primary">Install app</span>
                  </motion.div>
                  <div className="h-2 w-14 bg-muted-foreground/20 rounded mt-1" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex justify-center gap-3">
        <div className={cn(
          "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-colors",
          step === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
        )}>
          <span>1. Open menu</span>
        </div>
        <div className={cn(
          "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-colors",
          step === 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
        )}>
          <span>2. Install app</span>
        </div>
      </div>
    </div>
  );
}
